import { ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { StatusDueDate } from '@/components/DueDate';
import AlertBar from '@/design_system/AlertBar';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { ARTICLE_CANCELLATION_REASONS } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import useViewPort from '@/utils/useViewport';

export const BaseMessage = ({
  title,
  message,
  article,
  titleAction: action,
}: {
  title?: ReactNode;
  message?: ReactNode;
  article: ArticleWithRelations;
  titleAction?: ReactNode;
}) => {
  const { isMobile } = useViewPort();
  const { i18n, t } = useLingui();

  if (article.cancellationDetail && article.step?.step !== 'validation') {
    const cancelledTitle = article.quoteRefusedAt
      ? t({
          id: 'article.step-task-message.cancelled.title.estimate',
          message: 'The quote has been refused by the client',
        })
      : t({
          id: 'article.step-task-message.cancelled.title.payment',
          message: 'The final quote has been refused by the client',
        });

    return (
      <AlertBar type="error" size="large" hideIcon title={cancelledTitle}>
        <div>
          <Trans id="article.step-task-message.cancelled.text">Client refusal reason:</Trans> &quot;
          {article.cancellationDetail.reason === 'other' ||
          article.cancellationDetail.reason === 'store-other' ? (
            <Trans id="article.step-task-message.cancelled.text.other">
              Other - {article.cancellationDetail.otherReason}
            </Trans>
          ) : (
            i18n._(
              ARTICLE_CANCELLATION_REASONS.find(
                ({ id }) => article.cancellationDetail!.reason === id
              )!.label
            )
          )}
          &quot;
        </div>
        {!!message && <div>{message}</div>}
      </AlertBar>
    );
  }

  return (
    <AlertBar
      type={article.taskType ? 'warning' : 'info'}
      size="large"
      hideIcon
      title={
        <Stack
          row
          gap="1rem"
          justifyContent={isMobile ? 'space-between' : 'flex-start'}
          flexWrap="nowrap"
        >
          <Paragraph weight="medium">{title}</Paragraph>

          {!isMobile && (
            <div
              style={{ width: '1px', height: '1rem', marginTop: '0.25rem' }}
              className="bg-neutral-400"
            />
          )}

          <Stack row gap={isMobile ? '0.5rem' : '1rem'} flexWrap="nowrap">
            {!!article.statusDueAt && (
              <StatusDueDate
                date={new Date(article.statusDueAt)}
                displayDayMonthOnly
                variant="row"
              />
            )}

            {action ?? undefined}
          </Stack>
        </Stack>
      }
    >
      {message}
    </AlertBar>
  );
};

export const BaseMessageDetailedContent = ({
  children,
  article,
}: {
  article: ArticleWithRelations;
  children: ReactNode;
}) => {
  return (
    <Paragraph
      whiteSpace="pre-wrap"
      flex="1"
      style={{
        backgroundColor: article.taskType
          ? 'var(--color-warning-100)'
          : 'var(--color-tertiary-100)',
        borderRadius: '8px',
        padding: '8px 12px',
        overflowWrap: 'break-word',
      }}
    >
      {children}
    </Paragraph>
  );
};
